import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Minus, Plus, ContactlessPayment, Key, SpinnerGap, X, PaintBrush } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg, ugx, fmtDate } from "@/lib/api";
import { PageHead } from "./Properties";
import { PortraitPreview } from "./PortraitPreview";

export const ORDER_STATUS_CLS = { Received: "bg-secondary text-foreground", "In production": "bg-[#FBEBD2] text-[#8A5A12]", Dispatched: "bg-[#DCEBEF] text-[#2F6576]", Delivered: "bg-[#E3EEE2] text-[#3E6B3C]", Cancelled: "bg-[#F4D4D4] text-[#9B2C2C]" };
const PAYS = ["MTN MoMo", "Airtel Money", "Bank transfer", "Cash"];

function Qty({ icon: Icon, label, price, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border p-4">
      <div className="flex items-center gap-3"><span className="grid h-10 w-10 place-items-center rounded-lg bg-[#F3E3D9] text-[#D96C4A]"><Icon size={20} /></span><div><p className="font-semibold">{label}</p><p className="text-xs text-muted-foreground">{ugx(price)} each</p></div></div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => onChange(Math.max(0, value - 1))} data-testid={`${testId}-minus`} className="grid h-9 w-9 place-items-center rounded-lg border border-border"><Minus /></button>
        <span className="w-8 text-center font-display text-xl" data-testid={`${testId}-qty`}>{value}</span>
        <button type="button" onClick={() => onChange(value + 1)} data-testid={`${testId}-plus`} className="grid h-9 w-9 place-items-center rounded-lg border border-border"><Plus /></button>
      </div>
    </div>
  );
}

export default function Orders() {
  const [s, setS] = useState(null);
  const [orders, setOrders] = useState([]);
  const location = useLocation();
  const incoming = location.state?.design;
  const [o, setO] = useState(incoming ? { cards: incoming.kind === "card" ? 1 : 0, keyholders: incoming.kind === "keyholder" ? 1 : 0, delivery: "kampala", address: "", phone: "", pay: "MTN MoMo", note: "" }
    : { cards: 1, keyholders: 2, delivery: "kampala", address: "", phone: "", pay: "MTN MoMo", note: "" });
  const [designs, setDesigns] = useState(incoming ? [incoming] : []);
  const [busy, setBusy] = useState(false);
  const [props, setProps] = useState([]);
  const attach = (dz) => {
    setDesigns((x) => (x.some((y) => y.kind === dz.kind && y.property_id === dz.property_id) ? x : [...x, dz]));
    setO((x) => ({ ...x, cards: Math.max(1, x.cards) }));
    toast.success("Portrait card added to your order");
  };
  useEffect(() => {
    api.get("/properties").then((r) => setProps(r.data));
    api.get("/pricing").then((r) => setS(r.data));
    api.get("/orders").then((r) => {
      setOrders(r.data);
      const last = r.data[0];
      if (last) setO((x) => ({ ...x, phone: x.phone || last.phone, address: x.address || last.address, delivery: last.delivery, pay: last.pay }));
    });
    if (incoming) window.history.replaceState({}, "");
  }, [incoming]);
  if (!s) return null;
  const fees = { pickup: 0, kampala: s.delivery_kampala, upcountry: s.delivery_upcountry };
  const sub = o.cards * s.card_price + o.keyholders * s.keyholder_price;
  const total = sub + fees[o.delivery];
  const set = (k) => (v) => setO((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/orders", { ...o, designs: designs.map(({ kind, property_id, kh_id }) => ({ kind, property_id, kh_id })) });
      setOrders((x) => [r.data, ...x]);
      toast.success(`Order ${r.data.number} placed. We'll be in touch about payment and delivery.`);
      setO((x) => ({ ...x, note: "" }));
      setDesigns([]);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="orders-page">
      <PageHead eyebrow="Shop" title="Order NFC cards & key holders" />
      <div className="mb-8"><PortraitPreview properties={props} onAttach={attach} /></div>
      <div className="grid gap-8 lg:grid-cols-12">
        <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-white p-6 lg:col-span-7" data-testid="order-form">
          {designs.length > 0 && (
            <div className="space-y-2 rounded-xl border-2 border-dashed border-[#D96C4A]/50 bg-[#FBF1EC] p-4" data-testid="order-attached-designs">
              <p className="label-xs text-[#C25D3E]">Your design is attached</p>
              {designs.map((dz, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-sm" data-testid={`order-design-${i}`}>
                  <span className="flex items-center gap-2"><PaintBrush className="text-[#D96C4A]" /><b>{{ card: "NFC card", portrait: "Portrait NFC card", keyholder: "Key holder" }[dz.kind]}</b> · {dz.property_name} · {dz.label}</span>
                  <button type="button" onClick={() => setDesigns((x) => x.filter((_, j) => j !== i))} data-testid={`order-design-remove-${i}`} className="text-muted-foreground hover:text-destructive"><X /></button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">We print exactly this design with your property name and QR code.</p>
            </div>
          )}
          <Qty icon={ContactlessPayment} label="NFC cards" price={s.card_price} value={o.cards} onChange={set("cards")} testId="order-cards" />
          <Qty icon={Key} label="NFC key holders" price={s.keyholder_price} value={o.keyholders} onChange={set("keyholders")} testId="order-keyholders" />
          <div className="space-y-2">
            <Label>Delivery</Label>
            {[["pickup", "Pick up in Kampala"], ["kampala", "Delivery in Kampala & Entebbe"], ["upcountry", "Courier upcountry"]].map(([k, l]) => (
              <label key={k} className={`flex cursor-pointer items-center justify-between rounded-xl border-2 px-4 py-3 text-sm transition-colors duration-200 ${o.delivery === k ? "border-[#D96C4A]" : "border-border"}`} data-testid={`order-delivery-${k}`}>
                <span className="flex items-center gap-3"><input type="radio" name="delivery" checked={o.delivery === k} onChange={() => set("delivery")(k)} className="accent-[#D96C4A]" />{l}</span>
                <span className="font-semibold">{fees[k] ? ugx(fees[k]) : "Free"}</span>
              </label>
            ))}
          </div>
          {o.delivery !== "pickup" && <div className="space-y-1.5"><Label>Delivery address</Label><Textarea rows={2} value={o.address} onChange={(e) => set("address")(e.target.value)} data-testid="order-address-input" /></div>}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Phone</Label><Input value={o.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+256 7…" data-testid="order-phone-input" /></div>
            <div className="space-y-1.5"><Label>Payment method</Label>
              <select value={o.pay} onChange={(e) => set("pay")(e.target.value)} data-testid="order-pay-select" className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm">{PAYS.map((p) => <option key={p}>{p}</option>)}</select></div>
          </div>
          <div className="space-y-1.5"><Label>Note (optional)</Label><Textarea rows={2} value={o.note} onChange={(e) => set("note")(e.target.value)} placeholder="e.g. which Airbnb each key holder is for" data-testid="order-note-input" /></div>
          <div className="rounded-xl bg-secondary p-4 text-sm" data-testid="order-summary">
            <div className="flex justify-between"><span>Items</span><span>{ugx(sub)}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span>{ugx(fees[o.delivery])}</span></div>
            <div className="mt-2 flex justify-between border-t border-border pt-2 font-display text-xl"><span>Total</span><span data-testid="order-total">{ugx(total)}</span></div>
          </div>
          <button disabled={busy} data-testid="order-submit-button" className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#D96C4A] font-semibold text-white hover:bg-[#C25D3E] transition-colors duration-200 disabled:opacity-60">{busy && <SpinnerGap className="animate-spin" />} Place order</button>
          <p className="text-xs text-muted-foreground">No payment is taken here. We'll confirm and you pay by your chosen method.</p>
        </form>
        <section className="lg:col-span-5">
          <h2 className="font-display text-xl">Your orders</h2>
          <div className="mt-4 space-y-3" data-testid="order-history">
            {orders.length === 0 && <p className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">No orders yet.</p>}
            {orders.map((x) => (
              <div key={x.order_id} className="rounded-2xl border border-border bg-white p-4" data-testid={`order-${x.order_id}`}>
                <div className="flex items-center justify-between"><span className="font-mono text-sm font-semibold">{x.number}</span><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ORDER_STATUS_CLS[x.status]}`} data-testid={`order-status-${x.order_id}`}>{x.status}</span></div>
                <p className="mt-2 text-sm">{x.cards} card{x.cards === 1 ? "" : "s"} · {x.keyholders} key holder{x.keyholders === 1 ? "" : "s"} · {x.delivery_label}</p>
                {x.designs?.length > 0 && <p className="mt-1 flex items-center gap-1 text-xs text-[#C25D3E]" data-testid={`order-designs-${x.order_id}`}><PaintBrush /> {x.designs.map((dz) => `${{ card: "Card", portrait: "Portrait card", keyholder: "Key holder" }[dz.kind]}: ${dz.property_name}${dz.kind === "keyholder" ? ` (${dz.label})` : ""}`).join(" · ")}</p>}
                <p className="mt-1 text-xs text-muted-foreground">{fmtDate(x.created_at)} · {ugx(x.total)} · {x.pay}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
